/**
 * useWorkspaceSwitcher Hook
 *
 * Encapsulates the workspace switch pattern: update store + navigate.
 * Used by DashboardView, AppShell, and UserProfileMenu.
 *
 * @param targetPath - Fixed route segment to navigate to (e.g., 'dashboard').
 *   If omitted, preserves the current sub-path from the URL.
 *
 * @module hooks/useWorkspaceSwitcher
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAllArtifacts } from '@/artifacts';
import { useGeoMarkupStore } from '@/store';
import { settingsService } from '@/services/api/settingsService';
import { MAP_CONFIG_SECTION } from '@/routes/routes';

/** Index where the workspace sub-path begins in /w/{shortId}/... */
const SUBPATH_START_INDEX = 3;
const DEFAULT_SUBPATH = 'dashboard';

export function useWorkspaceSwitcher(targetPath?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const setCurrentWorkspace = useGeoMarkupStore(
    (state) => state.auth.setCurrentWorkspace
  );
  const requestFormGuard = useGeoMarkupStore(
    (state) => state.formConflict.request
  );
  const closeAll = useGeoMarkupStore((state) => state.ui.closeAll);

  const resolvedPath = useMemo(() => {
    if (targetPath) return targetPath;
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Preserve full sub-path so /w/A/map/tasks → /w/B/map/tasks
    // (not just /w/B/map). If the section is an overlay route
    // (/w/A/map/task/abc), fall back to /map to avoid carrying an
    // ID that may not exist in the target workspace.
    const subSegments = pathParts.slice(SUBPATH_START_INDEX - 1);
    if (subSegments.length === 0) return DEFAULT_SUBPATH;

    // map-config is a first-run/edit gate route — never carry it to another
    // workspace; fall back to the dashboard (the AppShell gate re-redirects to
    // map-config if the target workspace is itself unconfigured).
    if (subSegments[0] === MAP_CONFIG_SECTION) return DEFAULT_SUBPATH;

    // Detect overlay routes and strip them.
    // Expected shape: ['map', 'task'|'note', <id-or-new>, ...]
    if (
      subSegments[0] === 'map' &&
      getAllArtifacts().some((a) => a.singular === subSegments[1])
    ) {
      return 'map/tasks';
    }
    return subSegments.join('/');
  }, [targetPath, location.pathname]);

  return useCallback(
    (workspaceId: string, shortId: string) => {
      // Switching workspace must close any open form. Route through the global
      // unsaved-changes guard: a dirty form prompts before discard, then
      // `closeAll` tears down every open panel (incl. the share list, fixing
      // its stale-data-after-switch bug) before navigating.
      requestFormGuard(() => {
        closeAll();
        setCurrentWorkspace(workspaceId);
        settingsService
          .update('last_workspace', { value: workspaceId })
          .catch((err) =>
            console.warn('Failed to persist last workspace', err)
          );
        navigate(`/w/${shortId}/${resolvedPath}`);
      });
    },
    [requestFormGuard, closeAll, setCurrentWorkspace, navigate, resolvedPath]
  );
}
